// v49.50 — SearchPalette.
// Cmd-K (Ctrl-K on Windows/Linux) opens a global search modal that
// hits the search_platform RPC (migration-036). Results are sectioned
// by entity type, keyboard-navigable, and each row deep-links to the
// relevant page.
//
// The RPC is SECURITY INVOKER, so the user only sees results their
// existing RLS policies already permit. No new gating needed here.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { supabase } from "@/lib/api/supabase-client";

export type SearchResultType = "operative" | "company" | "project" | "requirement" | "deal" | "invoice";

interface SearchResultRow {
  result_type: SearchResultType;
  result_id:   string;
  title:       string;
  subtitle:    string | null;
  link:        string;
  rank:        number;
}

interface Props {
  open:    boolean;
  onClose: () => void;
}

const TYPE_LABELS: Record<SearchResultType, string> = {
  operative:   "Operatives",
  company:     "Companies",
  project:     "Projects",
  requirement: "Requirements",
  deal:        "Deals",
  invoice:     "Invoices",
};

const TYPE_ORDER: SearchResultType[] = ["operative", "company", "project", "requirement", "deal", "invoice"];

export function SearchPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResultRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);

  /* Focus input on open + reset state. */
  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
    setSelected(0);
    /* Slight delay so the modal mounts first; otherwise iOS Safari
       sometimes drops the focus. */
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  /* v50.239 — debounce reduced 300ms → 80ms so the results panel
     repaints essentially in time with the user's keystrokes ("auto
     narrow as I type" UX). 80ms is still enough to coalesce a burst
     of fast typing into one RPC call rather than firing per
     character. AbortController cancels any in-flight RPC when the
     query changes, so late responses don't overwrite a fresher
     result set. */
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const ac = new AbortController();
    const handle = setTimeout(async () => {
      const { data, error } = await supabase
        .rpc("search_platform", { q: trimmed, limit_n: 24 })
        .abortSignal(ac.signal);
      if (ac.signal.aborted) return;
      if (error) {
        console.warn("[search] rpc error", error);
        setResults([]); setLoading(false); return;
      }
      setResults((data as SearchResultRow[]) || []);
      setSelected(0);
      setLoading(false);
    }, 80);
    return () => { ac.abort(); clearTimeout(handle); };
  }, [q, open]);

  /* Section the flat result list by type while preserving rank order
     within each section. */
  const sections = useMemo(() => {
    const grouped = new Map<SearchResultType, SearchResultRow[]>();
    for (const r of results) {
      const existing = grouped.get(r.result_type) || [];
      existing.push(r);
      grouped.set(r.result_type, existing);
    }
    return TYPE_ORDER
      .filter(t => grouped.has(t))
      .map(t => ({ type: t, rows: grouped.get(t) || [] }));
  }, [results]);

  /* Flatten back to indexed list for keyboard nav. */
  const flat = useMemo(
    () => sections.flatMap(s => s.rows),
    [sections],
  );

  const go = useCallback((row: SearchResultRow | undefined) => {
    if (!row) return;
    navigate(row.link);
    onClose();
  }, [navigate, onClose]);

  /* Keyboard handlers — ↑/↓ to move, ↵ to navigate, Esc to close. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected(s => (flat.length === 0 ? 0 : (s + 1) % flat.length));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected(s => (flat.length === 0 ? 0 : (s - 1 + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      go(flat[selected]);
      return;
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ background: "rgba(8,17,33,.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      onClick={onClose}
      onKeyDown={onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Search the platform"
    >
      <div
        className="w-full max-w-[620px] bg-white rounded-lc-lg shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="px-4 py-3 border-b border-lc-line flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-lc-mute shrink-0" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search operatives, partners, deals, invoices…"
            className="flex-1 bg-transparent text-base text-lc-ink placeholder:text-lc-faint outline-none font-semibold"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-lc-faint">…</span>
          )}
          <kbd className="hidden md:inline-block px-2 py-0.5 rounded-lc-sm border border-lc-line bg-lc-bg-2 text-[10px] font-extrabold text-lc-mute">
            Esc
          </kbd>
        </div>

        {/* v50.239 — live result count strip above the result list so
            it's obvious the search is narrowing as you type. Updates
            on every keystroke; hidden when the query is too short. */}
        {q.trim().length >= 2 && (
          <div className="px-4 pt-2 text-[10px] font-extrabold uppercase tracking-widest text-lc-faint flex items-center gap-2">
            <span>
              {loading
                ? "Searching…"
                : flat.length === 0
                ? "0 results"
                : flat.length === 1
                ? "1 result"
                : `${flat.length} results`}
            </span>
            {flat.length > 0 && !loading && (
              <span className="text-lc-mute font-bold normal-case tracking-normal">
                · across {sections.length} {sections.length === 1 ? "category" : "categories"}
              </span>
            )}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-6 py-10 text-center text-xs text-lc-mute">
              Type at least 2 characters. Cmd-K to reopen.
            </div>
          ) : !loading && flat.length === 0 ? (
            <div className="px-6 py-10 text-center text-xs text-lc-mute">
              No results for "{q.trim()}".
            </div>
          ) : (
            <ul className="py-1">
              {sections.map(section => (
                <li key={section.type}>
                  <div className="px-4 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-widest text-lc-faint">
                    {TYPE_LABELS[section.type]}
                  </div>
                  <ul>
                    {section.rows.map(row => {
                      const idx = flat.indexOf(row);
                      const active = idx === selected;
                      return (
                        <li key={`${row.result_type}:${row.result_id}`}>
                          <button
                            type="button"
                            onClick={() => go(row)}
                            onMouseEnter={() => setSelected(idx)}
                            className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                              active ? "bg-lc-blue-light" : "hover:bg-lc-bg-2/50"
                            }`}
                          >
                            <TypeIcon type={row.result_type} />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-extrabold text-lc-ink truncate">{row.title}</span>
                              {row.subtitle && (
                                <span className="block text-[11px] text-lc-mute truncate">{row.subtitle}</span>
                              )}
                            </span>
                            <span
                              className="shrink-0 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border"
                              style={typeTint(row.result_type)}
                            >
                              {TYPE_LABELS[row.result_type].replace(/s$/, "")}
                            </span>
                            {active && (
                              <kbd className="hidden md:inline-block px-2 py-0.5 rounded-lc-sm border border-lc-blue-border bg-white text-[10px] font-extrabold text-lc-blue-dark">
                                ↵
                              </kbd>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-lc-line bg-lc-bg-soft flex items-center justify-between gap-3 text-[10px] font-extrabold uppercase tracking-widest text-lc-faint">
          <span>Navigate with ↑ ↓ · open with ↵</span>
          <span className="hidden md:inline">⌘K to reopen</span>
        </div>
      </div>
    </div>
  );
}

function TypeIcon({ type }: { type: SearchResultType }) {
  const tint = typeTint(type);
  const common = {
    width: 16, height: 16, viewBox: "0 0 24 24", fill: "none",
    stroke: tint.color, strokeWidth: 2.2,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  return (
    <span className="w-8 h-8 rounded-lc-md flex items-center justify-center shrink-0 border" style={{ background: tint.background, borderColor: tint.borderColor }}>
      {type === "operative"   && <svg {...common}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>}
      {type === "company"     && <svg {...common}><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h.01M9 13h.01M9 17h.01M14 9h.01M14 13h.01M14 17h.01" /></svg>}
      {type === "project"     && <svg {...common}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>}
      {type === "requirement" && <svg {...common}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
      {type === "deal"        && <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
      {type === "invoice"     && <svg {...common}><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>}
    </span>
  );
}

function typeTint(type: SearchResultType): { background: string; borderColor: string; color: string } {
  switch (type) {
    case "operative":   return { background: "var(--color-lc-blue-light)", borderColor: "var(--color-lc-blue-border)", color: "var(--color-lc-blue-dark)" };
    case "company":     return { background: "var(--color-lc-green-bg)", borderColor: "#BBF7D0", color: "var(--color-lc-green)" };
    case "project":     return { background: "#F5F3FF", borderColor: "#DDD6FE", color: "#7C3AED" };
    case "requirement": return { background: "var(--color-lc-amber-bg)", borderColor: "#FDE68A", color: "var(--color-lc-amber)" };
    case "deal":        return { background: "#F0F9FF", borderColor: "#BAE6FD", color: "#0369A1" };
    case "invoice":     return { background: "#FEF2F2", borderColor: "#FECACA", color: "#B91C1C" };
  }
}
